import type { ReactNode } from "react";
import { ActiveFilters, FiltersButton } from "@amber/ui";
import { HEALTH_LABELS, platformEntry, type SourceHealthKey, type SourcesResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { SourcesSearch } from "../search";

export interface SourceChip {
  label: string;
  value: string;
  clear: Partial<SourcesSearch>;
}

export function sourceChips(search: SourcesSearch, data: SourcesResponse | undefined): SourceChip[] {
  return [
    ...listOf(search.health).map((h) => ({
      label: "Health",
      value: HEALTH_LABELS[h as SourceHealthKey],
      clear: { health: toggled(search.health, h) },
    })),
    ...(search.platform
      ? [
          {
            label: "Platform",
            value: platformEntry(search.platform)?.label ?? search.platform,
            clear: { platform: undefined },
          },
        ]
      : []),
    ...(search.person
      ? [
          {
            label: "Person",
            value: data?.sources.flatMap((s) => s.people).find((p) => p.id === search.person)?.name ?? "Person",
            clear: { person: undefined },
          },
        ]
      : []),
    ...(search.source
      ? [
          {
            label: "Source",
            value: data?.sources.find((s) => s.id === search.source)?.title ?? "Source",
            clear: { source: undefined },
          },
        ]
      : []),
    ...(search.q ? [{ label: "Name", value: search.q, clear: { q: undefined } }] : []),
  ];
}

export function SourcesToolbar({
  count,
  tools,
  chips,
  filtersOpen,
  onFilters,
  set,
  onReset,
}: {
  count: ReactNode;
  tools: ReactNode;
  chips: readonly SourceChip[];
  filtersOpen: boolean;
  onFilters: () => void;
  set: (patch: Partial<SourcesSearch>) => void;
  onReset: () => void;
}) {
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-2 px-4 py-2 max-compact:px-2">
      <FiltersButton open={filtersOpen} onToggle={onFilters} />
      <span className="text-ui text-muted" role="status">
        {count}
      </span>
      <ActiveFilters
        filters={chips.map((c) => ({
          key: `${c.label}-${c.value}`,
          label: c.label,
          value: c.value,
          onRemove: () => {
            set(c.clear);
          },
        }))}
        onReset={onReset}
      />
      <span className="flex-1" />
      {tools}
    </div>
  );
}
